import { useMemo } from 'react';
import useSWR from 'swr';
import * as echarts from 'echarts';
import type { CryptoKlinePoint } from '@/type/crypto';
import { TranslationParams } from '@/lib/i18n';
import { formatDateTime } from '@/lib/utils/date';

export interface ForecastNodeDatum {
  value: [string, number];
  date: string;
  prices: [number, number, number, number, number];
  eventTitle: string;
  eventSlug: string;
  levels: ProbabilityConeLevel[];
}

export interface ForecastChartData {
  dates: string[];
  priceValues: number[];
  nodes: ForecastNodeDatum[];
  series: echarts.SeriesOption[];
}

export interface ForecastInteractionPointDatum {
  value: [string, number];
  forecastNodeIndex: number;
}

export type ProbabilityConeLevelLabel =
  | 'strong_resistance'
  | 'resistance'
  | 'expected'
  | 'support'
  | 'strong_support';

export interface ProbabilityConeLevel {
  prob: number;
  label: ProbabilityConeLevelLabel;
  price: number;
}

export interface ProbabilityConeRawPoint {
  price_level: number;
  yes_prob: number;
}

export interface ProbabilityConeSlice {
  date: string;
  end_ts: number;
  event_title: string;
  event_slug: string;
  levels: ProbabilityConeLevel[];
  raw_points: ProbabilityConeRawPoint[];
}

export interface ProbabilityConeResponse {
  symbol: string;
  supports_cone: boolean;
  slices: ProbabilityConeSlice[];
}

const EMPTY_FORECAST_CHART_DATA: ForecastChartData = {
  dates: [],
  priceValues: [],
  nodes: [],
  series: [],
};

const FORECAST_TOOLTIP_PERCENTILES = [10, 25, 50, 25, 10];

interface UseProbabilityConeParams {
  symbol: string;
  supportsCone?: boolean;
  klinePoints: CryptoKlinePoint[];
  anchorPoint?: CryptoKlinePoint;
}

function formatKlineDate(timestampSeconds: number) {
  return new Date(timestampSeconds * 1000).toISOString();
}

function formatPrice(price: number) {
  const absPrice = Math.abs(price);

  if (absPrice >= 1_000_000) return `${(price / 1_000_000).toFixed(1)}M`;
  if (absPrice >= 1_000) return `${(price / 1_000).toFixed(1)}K`;
  if (absPrice >= 100) return price.toFixed(1);
  if (absPrice >= 1) return price.toFixed(1);

  return price.toFixed(4);
}

function createForecastBand(
  forecastDates: string[],
  name: string,
  upper: number[],
  lower: number[],
  color: string,
  z: number,
): echarts.CustomSeriesOption {
  return {
    name,
    type: 'custom',
    coordinateSystem: 'cartesian2d',
    data: forecastDates.length ? [0] : [],
    clip: false,
    silent: false,
    z,
    renderItem: (_params, api) => {
      const upperPoints = forecastDates.map((date, index) => api.coord([date, upper[index]]));
      const lowerPoints = forecastDates
        .map((date, index) => api.coord([date, lower[index]]))
        .reverse();

      return {
        type: 'polygon',
        shape: {
          points: [...upperPoints, ...lowerPoints],
        },
        style: {
          fill: color,
        },
      };
    },
  };
}

function createForecastLine(
  forecastDates: string[],
  name: string,
  values: number[],
  color: string,
  opacity: number,
): echarts.CustomSeriesOption {
  return {
    name,
    type: 'custom',
    coordinateSystem: 'cartesian2d',
    data: forecastDates.length ? [0] : [],
    clip: false,
    silent: true,
    z: 4,
    renderItem: (_params, api) => {
      const points = forecastDates.map((date, index) => api.coord([date, values[index]]));

      return {
        type: 'polyline',
        shape: { points },
        style: {
          fill: 'none',
          stroke: color,
          lineWidth: 1,
          opacity,
          lineDash: [2, 2],
        },
      };
    },
  };
}

function createForecastInteractionPoints(
  forecastDates: string[],
  name: string,
  values: number[],
  color: string,
): echarts.CustomSeriesOption {
  return {
    name,
    type: 'custom',
    coordinateSystem: 'cartesian2d',
    data: forecastDates.map(
      (date, index): ForecastInteractionPointDatum => ({
        value: [date, values[index]],
        forecastNodeIndex: index - 1,
      }),
    ),
    clip: false,
    z: 6,
    renderItem: (params, api) => {
      const point = api.coord([api.value(0), api.value(1)]);
      const isFirstPoint = params.dataIndex === 0;

      return {
        type: 'circle',
        shape: { cx: point[0], cy: point[1], r: 4 },
        style: {
          fill: isFirstPoint ? '#46D887' : '#030303',
          stroke: isFirstPoint ? '#46D887' : color,
          lineWidth: isFirstPoint ? 0 : 2,
          opacity: isFirstPoint ? 1 : 0,
        },
        emphasis: {
          style: {
            opacity: 1,
          },
        },
      };
    },
  };
}

function findLevelPrice(slice: ProbabilityConeSlice, label: ProbabilityConeLevelLabel) {
  return slice.levels.find((level) => level.label === label)?.price;
}

function findProbabilityConeLevel(
  levels: ProbabilityConeLevel[],
  label: ProbabilityConeLevelLabel,
) {
  return levels.find((level) => level.label === label);
}

function hasCompleteLevels(slice: ProbabilityConeSlice) {
  return (
    findLevelPrice(slice, 'strong_support') !== undefined &&
    findLevelPrice(slice, 'support') !== undefined &&
    findLevelPrice(slice, 'expected') !== undefined &&
    findLevelPrice(slice, 'resistance') !== undefined &&
    findLevelPrice(slice, 'strong_resistance') !== undefined
  );
}

function createForecastChartData({
  showForecast,
  probabilityConeData,
  anchorPoint,
}: {
  showForecast: boolean;
  probabilityConeData?: ProbabilityConeResponse;
  anchorPoint?: CryptoKlinePoint;
}): ForecastChartData {
  if (!showForecast || !anchorPoint) return EMPTY_FORECAST_CHART_DATA;

  const slices =
    probabilityConeData?.slices
      ?.filter(
        (slice) =>
          Number.isFinite(slice.end_ts) && slice.end_ts > anchorPoint.t && hasCompleteLevels(slice),
      )
      .toSorted((a, b) => a.end_ts - b.end_ts) ?? [];

  if (!slices.length) return EMPTY_FORECAST_CHART_DATA;

  const anchorPrice = anchorPoint.p;
  const dates = [
    formatKlineDate(anchorPoint.t),
    ...slices.map((slice) => formatKlineDate(slice.end_ts)),
  ];
  const outerLowerForecast = [
    anchorPrice,
    ...slices.map((slice) => findLevelPrice(slice, 'strong_support') ?? 0),
  ];
  const innerLowerForecast = [
    anchorPrice,
    ...slices.map((slice) => findLevelPrice(slice, 'support') ?? 0),
  ];
  const middleForecast = [
    anchorPrice,
    ...slices.map((slice) => findLevelPrice(slice, 'expected') ?? 0),
  ];
  const innerUpperForecast = [
    anchorPrice,
    ...slices.map((slice) => findLevelPrice(slice, 'resistance') ?? 0),
  ];
  const outerUpperForecast = [
    anchorPrice,
    ...slices.map((slice) => findLevelPrice(slice, 'strong_resistance') ?? 0),
  ];
  const nodes: ForecastNodeDatum[] = slices.map((slice, index) => ({
    value: [dates[index + 1], middleForecast[index + 1]],
    date: dates[index + 1],
    prices: [
      outerLowerForecast[index + 1],
      innerLowerForecast[index + 1],
      middleForecast[index + 1],
      innerUpperForecast[index + 1],
      outerUpperForecast[index + 1],
    ],
    eventTitle: slice.event_title,
    eventSlug: slice.event_slug,
    levels: slice.levels,
  }));
  const series: echarts.SeriesOption[] = [
    createForecastBand(
      dates,
      'Forecast range',
      outerUpperForecast,
      outerLowerForecast,
      'rgba(114, 111, 80, .46)',
      2,
    ),
    createForecastBand(
      dates,
      'Forecast core',
      innerUpperForecast,
      innerLowerForecast,
      'rgba(227, 143, 31, .58)',
      3,
    ),
    createForecastLine(
      dates,
      'Forecast outer upper visual',
      outerUpperForecast,
      'rgba(249, 184, 76, .36)',
      1,
    ),
    createForecastLine(
      dates,
      'Forecast upper visual',
      innerUpperForecast,
      'rgba(249, 184, 76, .62)',
      1,
    ),
    createForecastLine(dates, 'Forecast mid visual', middleForecast, 'rgba(249, 184, 76, .72)', 1),
    createForecastLine(
      dates,
      'Forecast lower visual',
      innerLowerForecast,
      'rgba(249, 184, 76, .46)',
      1,
    ),
    createForecastLine(
      dates,
      'Forecast outer lower visual',
      outerLowerForecast,
      'rgba(249, 184, 76, .3)',
      1,
    ),
    createForecastInteractionPoints(dates, 'Forecast outer upper', outerUpperForecast, '#FF775F'),
    createForecastInteractionPoints(dates, 'Forecast upper', innerUpperForecast, '#F5BA80'),
    createForecastInteractionPoints(dates, 'Forecast mid', middleForecast, '#46D887'),
    createForecastInteractionPoints(dates, 'Forecast lower', innerLowerForecast, '#F5BA80'),
    createForecastInteractionPoints(dates, 'Forecast outer lower', outerLowerForecast, '#FF775F'),
  ];
  return {
    dates,
    priceValues: [
      ...outerUpperForecast,
      ...outerLowerForecast,
      ...innerUpperForecast,
      ...innerLowerForecast,
      ...middleForecast,
    ],
    nodes,
    series,
  };
}

export function createProbabilityConeTooltip(
  marker: ForecastNodeDatum,
  symbol: string,
  t: (key: string, params?: TranslationParams | undefined) => string,
) {
  const displayDate = formatDateTime(marker.date, 'MMM DD, YYYY hh:mm A');
  const levels = [
    findProbabilityConeLevel(marker.levels, 'strong_resistance'),
    findProbabilityConeLevel(marker.levels, 'resistance'),
    findProbabilityConeLevel(marker.levels, 'expected'),
    findProbabilityConeLevel(marker.levels, 'support'),
    findProbabilityConeLevel(marker.levels, 'strong_support'),
  ].filter((level): level is ProbabilityConeLevel => Boolean(level));
  const levelColor: Record<ProbabilityConeLevelLabel, string> = {
    strong_resistance: '#FF5C3F',
    resistance: '#F7B33E',
    expected: '#46D887',
    support: '#F7B33E',
    strong_support: '#FF5C3F',
  };

  return `
    <div style="width:308px;border-radius:8px;background:#191919;color:#8C8C8C;box-shadow:0 12px 36px rgba(0,0,0,.45);overflow:hidden;font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">
      <div style="padding:14px 16px 6px;display:flex;align-items:center;gap:8px;font-size:12px;">
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <rect x="3" y="4" width="10" height="9" rx="1.5" fill="none" stroke="#8A8A8A" stroke-width="1.2"/>
          <path d="M5 2.5v3M11 2.5v3M3.5 7h9" stroke="#8A8A8A" stroke-width="1.2" stroke-linecap="round"/>
        </svg>
        <span>${displayDate}</span>
      </div>
      <div style="height:126px;padding:0 10px;">
        <svg viewBox="0 0 288 126" width="288" height="126" style="display:block;">
          <defs>
            <linearGradient id="forecastTooltipFill" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stop-color="#151817"/>
              <stop offset="28%" stop-color="#18251F"/>
              <stop offset="50%" stop-color="#253221"/>
              <stop offset="72%" stop-color="#18251F"/>
              <stop offset="100%" stop-color="#151817"/>
            </linearGradient>
            <linearGradient id="forecastTooltipAxis" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stop-color="#E76652"/>
              <stop offset="28%" stop-color="#D99A52"/>
              <stop offset="50%" stop-color="#49D786"/>
              <stop offset="72%" stop-color="#D99A52"/>
              <stop offset="100%" stop-color="#E76652"/>
            </linearGradient>
          </defs>
          <path d="M18 108 C64 103 82 78 104 43 C122 15 166 15 184 43 C206 78 224 103 270 108 L270 109 L18 109 Z" fill="url(#forecastTooltipFill)" opacity=".86"/>
          <path d="M18 108 C64 103 82 78 104 43 C122 15 166 15 184 43 C206 78 224 103 270 108" fill="none" stroke="#8C928D" stroke-width="1.6"/>
          <rect x="18" y="108" width="252" height="2" rx="1" fill="url(#forecastTooltipAxis)"/>
          <line x1="48" y1="101" x2="48" y2="108" stroke="#8E8E8E" stroke-width="1" stroke-dasharray="2 2" opacity=".65"/>
          <line x1="102" y1="46" x2="102" y2="108" stroke="#8E8E8E" stroke-width="1" stroke-dasharray="2 2" opacity=".65"/>
          <line x1="144" y1="24" x2="144" y2="108" stroke="#8E8E8E" stroke-width="1" stroke-dasharray="2 2" opacity=".65"/>
          <line x1="186" y1="46" x2="186" y2="108" stroke="#8E8E8E" stroke-width="1" stroke-dasharray="2 2" opacity=".65"/>
          <line x1="240" y1="101" x2="240" y2="108" stroke="#8E8E8E" stroke-width="1" stroke-dasharray="2 2" opacity=".65"/>
        </svg>
      </div>
      <div style="padding:0 12px 12px;">
        <div style="font-size:11px;color:#777;margin-bottom:6px;">${t('Percentile')}</div>
        <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:4px;text-align:center;font-size:11px;">
          ${levels
            .map(
              (level, index) =>
                `<div><div style="color:#9A9A9A;">${FORECAST_TOOLTIP_PERCENTILES[index] ?? 50}%</div><div style="color:${levelColor[level.label]};font-size:12px;">${formatPrice(level.price)}</div></div>`,
            )
            .join('')}
        </div>
      </div>
      <div style="height:1px;background:#252525;"></div>
      <div style="display:flex;align-items:center;gap:8px;padding:12px 16px 14px;font-size:12px;color:#D5D5D5;">
        <span style="display:inline-flex;width:14px;height:14px;border-radius:50%;align-items:center;justify-content:center;background:#D17A1D;color:white;font-size:10px;font-weight:700;">B</span>
        <span>${t('Source')}:</span>
        <a style="flex:1;color:#D5D5D5;text-decoration:none;" href="/predic/market?id=${marker.eventSlug}" onmouseenter="this.style.color='#A772FF';this.style.textDecoration='underline';" onmouseleave="this.style.color='#D5D5D5';this.style.textDecoration='none';">${marker.eventTitle} ></a>
      </div>
    </div>
  `;
}

export function useProbabilityCone({
  symbol,
  supportsCone,
  klinePoints,
  anchorPoint,
}: UseProbabilityConeParams) {
  const probabilityCone = useSWR<ProbabilityConeResponse>(
    supportsCone ? `api:/market/crypto/${symbol}/probability-cone` : null,
  );
  const showForecast =
    supportsCone === true &&
    klinePoints.length > 0 &&
    anchorPoint !== undefined &&
    Boolean(probabilityCone.data?.slices?.length);
  const chartData = useMemo(
    () =>
      createForecastChartData({
        showForecast,
        probabilityConeData: probabilityCone.data,
        anchorPoint,
      }),
    [anchorPoint, probabilityCone.data, showForecast],
  );

  return {
    ...probabilityCone,
    showForecast,
    chartData,
    renderTooltip: createProbabilityConeTooltip,
  };
}
